import React, { useState, useEffect, useMemo, useCallback, useContext, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { ModalFilter } from "../../components/Modal/Filter/ModalFilter";
import { Title } from '../../components/UI/Custom/Title';
import { LoadingCircle } from '../../components/UI/Custom/LoadingCircle';
import { AddLeadModal } from '../../components/Modal/Lead/AddLeadModal';
import { fieldsConfigAdmin, fieldsConfigAffiliator } from '../../utils/forms/leadFields';
import { fetchLeads } from '../../utils/api/Lead/getLeads';
import styles from '../../styles/pages/Lead/LeadPage.module.css';
import { handleError } from '../../utils/custom/errorHandler';
import { Notification } from '../../components/UI/Notifications';
import { getMyAccountApi } from '../../utils/api/Account/getMyAccountApi';
import { PermissionDenied } from '../../components/UI/Custom/PermissionDenied';
import { formatDateTime } from '../../utils/custom/formatters';
import { DropdownSelector } from '../../components/UI/DropdownSelector';
import { MultiSelectDropdown } from '../../components/UI/MultiSelectDropdown';

import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import { sortOrders } from '../../utils/forms/sortOrder';
import { LangContext } from '../../context/LangContext';
import { ThemeContext } from '../../context/ThemeContext';
import { fieldLabels } from '../../utils/forms/fieldsLabels';
import { saveWithTTL, getWithTTL } from '../../utils/custom/localStorageTTL';

import { ConfirmationModal } from '../../components/UI/Custom/ConfirmationModal';
import { bulkDeleteLeadsApi } from '../../utils/api/Lead/bulkDeleteLeadsApi';
import { bulkHardDeleteLeadsApi } from '../../utils/api/Lead/bulkHardDeleteLeadsApi';
import { BulkUpdateModal } from '../../components/Modal/Lead/BulkUpdateModal';
import { ImportLeadsModal } from '../../components/Modal/Lead/ImportLeadsModal';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { ExportLeadsModal } from '../../components/Modal/Lead/ExportLeadsModal';
import { getGeoCountryName, getGeoFlagPath } from '../../utils/geoCountryNames';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import { loadLeadFilterOptions } from '../../utils/leadFilterOptions';
import { LeadCommentModal } from '../../components/Modal/Lead/LeadCommentModal';
import { LeadStatusModal } from '../../components/Modal/Lead/LeadStatusModal';
import { LeadManagerModal } from '../../components/Modal/Lead/LeadManagerModal';
import { LeadTableSettingsModal } from '../../components/Modal/Lead/LeadTableSettingsModal';
import { getLeadFieldAccessMode, LEAD_FIELD_ACCESS_MODE, normalizeLeadFieldAccessModes } from '../../utils/leadFieldAccess';

const ITEMS_PER_PAGE_KEY = 'leadsItemsPerPage';
const BASE_LEAD_STORAGE_KEY = 'leadFiltersState';
const SAVE_FILTERS_SETTING_KEY = 'leadSaveFiltersSetting';
const BASE_LEAD_COLUMNS_STORAGE_KEY = 'leadTableColumnsState';
const LEAD_COLUMNS_PREFERENCES_VERSION = 2;
const SEARCH_DEBOUNCE_MS = 350;
const SUPER_ADMIN_ROLE_NAME = 'Super Admin';

const translations = {
    ru: {
        title: 'Лиды',
        leadsFound: 'Лидов найдено',
        addLead: 'Добавить Лида',
        importLeads: 'Импортировать лидов',
        filters: 'Фильтры',
        searchPlaceholder: 'Поиск по ID, имени, email, телефону...',
        noLeadsFound: 'Лиды не найдены.',
        first: 'Первая',
        last: 'Последняя',
        cancel: 'Отмена',
        accessDenied: 'Доступ на просмотр страницы лидов отсутствует',
        perPage: (count) => `${count} лидов`,
        bulkActions: 'Действия',
        selectAll: 'Выбрать все на странице',
        bulkUpdateSelected: 'Изменить выбранное',
        confirmBulkDelete: (count) => `Вы уверены, что хотите удалить ${count} лидов?`,
        deleteConfirmTitle: 'Подтвердите удаление лидов',
        deleteConfirmText: 'Удалить выбранное',
        confirmBulkHardDelete: (count) => `Вы уверены, что хотите безвозвратно удалить ${count} лидов? Восстановить данные после этого будет невозможно.`,
        hardDeleteConfirmTitle: 'Безвозвратное удаление лидов',
        hardDeleteConfirmText: 'Удалить безвозвратно',
        deleting: 'Удаление...',
        bulkDeleteSuccess: (count) => `${count} лидов успешно удалено.`,
        exportLeads: 'Экспорт лидов',
        emptyCommentCell: 'Нет комментариев',
        tableSettings: 'Настройки таблицы',
    },
    en: {
        title: 'Leads',
        leadsFound: 'Leads found',
        addLead: 'Add Lead',
        importLeads: 'Import leads',
        filters: 'Filters',
        searchPlaceholder: 'Search by ID, name, email, phone...',
        noLeadsFound: 'No leads found.',
        first: 'First',
        last: 'Last',
        cancel: 'Cancel',
        accessDenied: 'Access to view the leads page is denied',
        perPage: (count) => `${count} leads`,
        bulkActions: 'Bulk Actions',
        selectAll: 'Select all on page',
        bulkUpdateSelected: 'Update selected',
        confirmBulkDelete: (count) => `Are you sure you want to delete ${count} leads?`,
        deleteConfirmTitle: 'Confirm Lead Deletion',
        deleteConfirmText: 'Delete selected',
        confirmBulkHardDelete: (count) => `Are you sure you want to permanently delete ${count} leads? This action cannot be undone.`,
        hardDeleteConfirmTitle: 'Permanently delete leads',
        hardDeleteConfirmText: 'Delete forever',
        deleting: 'Deleting...',
        bulkDeleteSuccess: (count) => `${count} leads successfully deleted.`,
        exportLeads: 'Export leads',
        emptyCommentCell: 'No comments',
        tableSettings: 'Table settings',
    },
};

const CLICKABLE_STATUS_KEYS = new Set(['sale_status', 'reten_status']);

const normalizeLeadStatus = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');

const STATUS_BADGE_CLASS_MAP = {
    new: 'statusBadgeGreen',
    callback: 'statusBadgeGreen',
    askedtocalllater: 'statusBadgeGreen',
    deposit: 'statusBadgeGreen',
    wronginfo: 'statusBadgeRed',
    wrongnumber: 'statusBadgeRed',
    spam: 'statusBadgeRed',
    wronglanguage: 'statusBadgeRed',
    under18: 'statusBadgeRed',
    underage: 'statusBadgeRed',
    overage: 'statusBadgeRed',
    notinportrait: 'statusBadgeRed',
    undercriteria: 'statusBadgeRed',
    wronggeo: 'statusBadgeRed',
    ukraine: 'statusBadgeRed',
    didntregister: 'statusBadgeRed',
    calledbyothercompany: 'statusBadgeRed',
    trash: 'statusBadgeRed',
    noanswer: 'statusBadgeBlue',
    noanswerni: 'statusBadgeBlue',
    neveranswer: 'statusBadgeBlue',
    voicemail: 'statusBadgeBlue',
    notinterested: 'statusBadgeBrown',
    test: 'statusBadgePink',
    nomoney: 'statusBadgeBrown',
    partialdeposit: 'statusBadgeGreen',
};

const getLeadStatusBadgeClassName = (statusValue) => {
    const normalizedStatus = normalizeLeadStatus(statusValue);

    if (!normalizedStatus) {
        return '';
    }

    if (STATUS_BADGE_CLASS_MAP[normalizedStatus]) {
        return STATUS_BADGE_CLASS_MAP[normalizedStatus];
    }

    if (normalizedStatus.includes('deposit') || normalizedStatus.includes('callback') || normalizedStatus.includes('calllater')) {
        return 'statusBadgeGreen';
    }

    if (
        normalizedStatus.includes('wrong')
        || normalizedStatus.includes('spam')
        || normalizedStatus.includes('trash')
        || normalizedStatus.includes('under')
        || normalizedStatus.includes('over')
        || normalizedStatus.includes('criteria')
        || normalizedStatus.includes('geo')
        || normalizedStatus.includes('register')
        || normalizedStatus.includes('othercompany')
    ) {
        return 'statusBadgeRed';
    }

    if (
        normalizedStatus.includes('noanswer')
        || normalizedStatus.includes('neveranswer')
        || normalizedStatus.includes('voicemail')
    ) {
        return 'statusBadgeBlue';
    }

    if (normalizedStatus.includes('test')) {
        return 'statusBadgePink';
    }

    if (normalizedStatus.includes('notinterested') || normalizedStatus.includes('money')) {
        return 'statusBadgeBrown';
    }

    return 'statusBadgeBlue';
};

const trimToWords = (text, maxWords) => {
    if (!text) return '';
    const words = text.split(/\s+/).filter(word => word.length > 0);
    if (words.length > maxWords) {
        return words.slice(0, maxWords).join(' ') + '...';
    }
    return text;
};

const getHeaderKeyBySortPrefix = (keyPrefix) => {
    switch (keyPrefix) {
        case 'ID': return 'id';
        case 'NAME': return 'first_name';
        case 'SALE_STATUS': return 'sale_status';
        case 'RETEN_STATUS': return 'reten_status';
        case 'MANAGER_NAME': return 'manager_id';
        case 'PHONE': return 'phone';
        case 'EMAIL': return 'email';
        case 'GEO': return 'geo';
        case 'REGISTRATION_DATE': return 'created_at';
        case 'AFFILIATOR': return 'affiliator_id';
        case 'LAST_COMMENT_DATE': return 'last_commented_at';
        case 'LAST_COMMENT_TEXT': return 'last_comment';
        case 'CAMPAIGN': return 'campaign';
        default: return null;
    }
};

const getSortConfigFromOrderBy = (orderBy) => {
    if (!orderBy) {
        return { key: null, direction: 'default' };
    }

    const normalizedOrderBy = String(orderBy).toUpperCase();
    const matchedSortOrder = sortOrders.find((order) => order.value.toUpperCase() === normalizedOrderBy);

    if (!matchedSortOrder) {
        return { key: null, direction: 'default' };
    }

    const parts = matchedSortOrder.value.split('_');
    const direction = parts.pop().toLowerCase();
    const keyPrefix = parts.join('_');
    const key = getHeaderKeyBySortPrefix(keyPrefix);

    if (!key) {
        return { key: null, direction: 'default' };
    }

    return { key, direction };
};

const areColumnPreferencesEqual = (left, right) => {
    if (!left || !right) {
        return false;
    }

    if (left.version !== right.version) {
        return false;
    }

    if (left.order.length !== right.order.length || left.visible.length !== right.visible.length) {
        return false;
    }

    return left.order.every((key, index) => key === right.order[index])
        && left.visible.every((key, index) => key === right.visible[index]);
};

const LEGACY_COLUMN_KEY_MAP = {
    comments: 'last_comment',
    comment_date: 'last_commented_at',
    aff: 'affiliator_id',
};

const normalizeStoredColumnKeys = (keys) => {
    const normalizedKeys = [];
    const seenKeys = new Set();

    keys.forEach((key) => {
        const normalizedKey = LEGACY_COLUMN_KEY_MAP[key] || key;

        if (seenKeys.has(normalizedKey)) {
            return;
        }

        seenKeys.add(normalizedKey);
        normalizedKeys.push(normalizedKey);
    });

    return normalizedKeys;
};

const normalizeColumnPreferences = (preferences, headers) => {
    const availableKeys = headers.map((header) => header.key);
    const defaultVisible = [...availableKeys];
    const isLegacyPreferences = preferences?.version !== LEAD_COLUMNS_PREFERENCES_VERSION;

    const sourceOrder = Array.isArray(preferences?.order) ? preferences.order : [];
    const order = normalizeStoredColumnKeys(sourceOrder);

    availableKeys.forEach((key) => {
        if (!order.includes(key)) {
            order.push(key);
        }
    });

    const sourceVisible = Array.isArray(preferences?.visible) ? preferences.visible : defaultVisible;
    const visible = normalizeStoredColumnKeys(sourceVisible);

    if (isLegacyPreferences) {
        availableKeys.forEach((key) => {
            if (!visible.includes(key)) {
                visible.push(key);
            }
        });
    }

    return {
        version: LEAD_COLUMNS_PREFERENCES_VERSION,
        order,
        visible: visible.length > 0 ? visible : defaultVisible,
    };
};

const getStoredColumnPreferences = (storageKey) => {
    try {
        const rawPreferences = localStorage.getItem(storageKey);
        return rawPreferences ? JSON.parse(rawPreferences) : null;
    } catch (error) {
        return null;
    }
};

const getPersistedLeadFilters = (filters) => {
    if (!filters || typeof filters !== 'object') {
        return filters;
    }

    const { search, ...persistedFilters } = filters;
    void search;

    return persistedFilters;
};

export const LeadPage = ({ role }) => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const currentTranslation = translations[langSelected] || translations.ru;
    const currentFieldLabels = fieldLabels[langSelected] || fieldLabels.ru;


    const LEAD_STORAGE_KEY_WITH_ROLE = `${BASE_LEAD_STORAGE_KEY}_${role}`;

    const [selectedLeads, setSelectedLeads] = useState(new Set());
    const [showBulkActions, setShowBulkActions] = useState(false);
    const [isBulkDeleteConfirmOpen, setIsBulkDeleteConfirmOpen] = useState(false);
    const [isBulkHardDeleteConfirmOpen, setIsBulkHardDeleteConfirmOpen] = useState(false);
    const [isBulkUpdating, setIsBulkUpdating] = useState(false);
    const [isBulkDeleting, setIsBulkDeleting] = useState(false);
    const [isBulkHardDeleting, setIsBulkHardDeleting] = useState(false);
    const [importResultMessage, setImportResultMessage] = useState('');
    const [totalItems, setTotalItems] = useState(0);

    const getInitialFilters = (storedFilters) => {
        const defaultFilters = {
            manager_ids: [],
            desk_ids: [],
            affiliator_ids: [],
            campaign_ids: [],
            reten_statuses: [],
            sale_statuses: [],
            geos: [],
            search: '',
            from_date: '',
            to_date: '',
            email: '',
            first_name: '',
            last_name: '',
            phone: '',
            order_by: '',
        };

        const shouldLoadSavedFilters = localStorage.getItem(SAVE_FILTERS_SETTING_KEY) !== 'false';

        if (!storedFilters || !shouldLoadSavedFilters) return defaultFilters;

        return {
            ...defaultFilters,
            ...storedFilters,
            manager_ids: Array.isArray(storedFilters.manager_ids) ? storedFilters.manager_ids : defaultFilters.manager_ids,
            desk_ids: Array.isArray(storedFilters.desk_ids) ? storedFilters.desk_ids : defaultFilters.desk_ids,
            affiliator_ids: Array.isArray(storedFilters.affiliator_ids) ? storedFilters.affiliator_ids : defaultFilters.affiliator_ids,
            campaign_ids: Array.isArray(storedFilters.campaign_ids) ? storedFilters.campaign_ids : defaultFilters.campaign_ids,
            reten_statuses: Array.isArray(storedFilters.reten_statuses) ? storedFilters.reten_statuses : defaultFilters.reten_statuses,
            sale_statuses: Array.isArray(storedFilters.sale_statuses) ? storedFilters.sale_statuses : defaultFilters.sale_statuses,
            geos: Array.isArray(storedFilters.geos) ? storedFilters.geos : defaultFilters.geos,
            search: defaultFilters.search,
            email: storedFilters.email || defaultFilters.email,
            first_name: storedFilters.first_name || defaultFilters.first_name,
            last_name: storedFilters.last_name || defaultFilters.last_name,
            phone: storedFilters.phone || defaultFilters.phone,
            order_by: storedFilters.order_by || defaultFilters.order_by,
        };
    };

    const [isActiveFilter, setIsActiveFilter] = useState(false);
    const [modalAddLead, setModalAddLead] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [itemsPerPage, setItemsPerPage] = useState(() => {
        const storedValue = localStorage.getItem(ITEMS_PER_PAGE_KEY);
        return storedValue ? parseInt(storedValue, 10) : 15;
    });
    const [leads, setLeads] = useState([]);
    const [totalPages, setTotalPages] = useState(1);

    const [filters, setFilters] = useState(() => {
        const storedFilters = getWithTTL(LEAD_STORAGE_KEY_WITH_ROLE);
        return getInitialFilters(storedFilters);
    });

    const [shouldUpdateLeads, setShouldUpdateLeads] = useState(false);
    const [errors, setErrors] = useState({});
    const [permissions, setPermissions] = useState({});
    const [isPermissionsLoaded, setIsPermissionsLoaded] = useState(false);
    const [leadFieldAccessModes, setLeadFieldAccessModes] = useState(() => normalizeLeadFieldAccessModes());

    const [isIpAdmin, setIsIpAdmin] = useState(false);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [sortConfig, setSortConfig] = useState(() => getSortConfigFromOrderBy(filters.order_by));
    const [searchInput, setSearchInput] = useState(() => filters.search || '');

    const leadsPerPageOptions = useMemo(() => [
        { label: currentTranslation.perPage(15), value: 15 },
        { label: currentTranslation.perPage(25), value: 25 },
        { label: currentTranslation.perPage(50), value: 50 },
        { label: currentTranslation.perPage(75), value: 75 },
        { label: currentTranslation.perPage(100), value: 100 },
    ], [currentTranslation]);

    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [selectedLeadForComment, setSelectedLeadForComment] = useState(null);
    const [selectedLeadStatusModal, setSelectedLeadStatusModal] = useState(null);
    const [selectedLeadManagerModal, setSelectedLeadManagerModal] = useState(null);
    const [toolbarFilterOptions, setToolbarFilterOptions] = useState({});
    const [isTableSettingsModalOpen, setIsTableSettingsModalOpen] = useState(false);
    const interactiveCellPointerRef = useRef({
        pointerType: null,
        startX: 0,
        startY: 0,
        moved: false,
    });
    const exportInitialFilters = useMemo(() => {
        const first = (arr) => (Array.isArray(arr) && arr.length > 0 ? String(arr[0]) : "");

        return {
            manager_id: first(filters.manager_ids),
            desk_id: first(filters.desk_ids),
            affiliator_id: first(filters.affiliator_ids),
            sale_status: first(filters.sale_statuses),
            reten_status: first(filters.reten_statuses),
            geo: first(filters.geos),
        };
    }, [filters]);

    const toolbarFilterConfigs = useMemo(() => {
        const baseFields = [
            { key: 'geo', formKey: 'geos', endpoint: '/common/lead/filters/geos' },
        ];

        if (role !== 'admin') {
            return baseFields;
        }

        return [
            ...baseFields,
            { key: 'sale_status', formKey: 'sale_statuses', endpoint: '/common/lead/filters/sale-statuses' },
            { key: 'reten_status', formKey: 'reten_statuses', endpoint: '/common/lead/filters/retention-statuses' },
            { key: 'manager_id', formKey: 'manager_ids' },
            { key: 'campaign', formKey: 'campaign_ids' },
        ];
    }, [role]);

    useEffect(() => {
        const fetchPermissions = async () => {
            try {
                const accountData = await getMyAccountApi();

                if (accountData && accountData.permissions) {

                    const allPermissions = accountData.permissions;

                    const permissionsObject = {};

                    allPermissions.forEach(permission => {
                        permissionsObject[permission.name] = permission;
                    });

                    setPermissions(permissionsObject);
                }

                setLeadFieldAccessModes(normalizeLeadFieldAccessModes(accountData?.lead_field_access));

                if (accountData && accountData.ip_admin !== undefined) {
                    setIsIpAdmin(!!accountData.ip_admin);
                } else {
                    setIsIpAdmin(false);
                }

                setIsSuperAdmin(accountData?.role?.name === SUPER_ADMIN_ROLE_NAME);

            } catch (error) {
                handleError(error.response?.data || error, setErrors);
                setIsIpAdmin(false);
                setIsSuperAdmin(false);
            } finally {
                setIsPermissionsLoaded(true);
            }
        };

        if (role === 'admin') {
            fetchPermissions();
        } else {
            setIsPermissionsLoaded(true);
        }
    }, [role]);

    const canViewLeadField = useCallback((fieldKey) => {
        if (role !== 'admin') {
            return true;
        }

        return getLeadFieldAccessMode(leadFieldAccessModes, fieldKey) !== LEAD_FIELD_ACCESS_MODE.HIDE;
    }, [leadFieldAccessModes, role]);

    const canEditLeadField = useCallback((fieldKey) => {
        if (role !== 'admin') {
            return true;
        }

        return getLeadFieldAccessMode(leadFieldAccessModes, fieldKey) === LEAD_FIELD_ACCESS_MODE.EDIT;
    }, [leadFieldAccessModes, role]);


    const tableHeaders = useMemo(() => {
        const config = role === 'admin' ? fieldsConfigAdmin : role === 'affiliator' ? fieldsConfigAffiliator : [];

        const canListAffiliators = role === 'admin' && permissions['user.list_affiliator_accounts'];
        const canListComments = role === 'admin' && permissions['lead.list_lead_comments'];

        return config
            .filter(header => {
                if ((header.key === 'last_commented_at' || header.key === 'last_comment') && role === 'admin' && !canListComments) {
                    return false;
                }

                if (role === 'admin' && !canViewLeadField(header.key)) {
                    return false;
                }

                const isAffiliatorColumn = header.key === 'affiliator_id';

                if (isAffiliatorColumn && role === 'admin') {
                    return header.status === 'active' && canListAffiliators;
                }

                return header.status === 'active' && header.key !== 'last_name';
            })
            .map(header => ({
                ...header,
                label: currentFieldLabels[header.key] || header.label
            }));
    }, [role, currentFieldLabels, permissions, canViewLeadField]);

    const leadColumnsStorageKey = useMemo(
        () => `${BASE_LEAD_COLUMNS_STORAGE_KEY}_${role}`,
        [role]
    );

    const [columnPreferences, setColumnPreferences] = useState(() => getStoredColumnPreferences(leadColumnsStorageKey) || { version: LEAD_COLUMNS_PREFERENCES_VERSION, order: [], visible: [] });

    useEffect(() => {
        setColumnPreferences(getStoredColumnPreferences(leadColumnsStorageKey) || { version: LEAD_COLUMNS_PREFERENCES_VERSION, order: [], visible: [] });
    }, [leadColumnsStorageKey]);

    useEffect(() => {
        if (role === 'admin' && !isPermissionsLoaded) {
            return;
        }

        if (tableHeaders.length === 0) {
            return;
        }

        setColumnPreferences((prevPreferences) => {
            const nextPreferences = normalizeColumnPreferences(prevPreferences, tableHeaders);

            return areColumnPreferencesEqual(prevPreferences, nextPreferences)
                ? prevPreferences
                : nextPreferences;
        });
    }, [role, isPermissionsLoaded, tableHeaders]);

    useEffect(() => {
        if (role === 'admin' && !isPermissionsLoaded) {
            return;
        }

        if (columnPreferences.order.length === 0) {
            return;
        }

        localStorage.setItem(leadColumnsStorageKey, JSON.stringify(columnPreferences));
    }, [role, isPermissionsLoaded, leadColumnsStorageKey, columnPreferences]);

    const visibleTableHeaders = useMemo(() => {
        if (tableHeaders.length === 0) {
            return [];
        }

        const normalizedPreferences = normalizeColumnPreferences(columnPreferences, tableHeaders);
        const headersMap = new Map(tableHeaders.map((header) => [header.key, header]));
        const visibleKeys = new Set(normalizedPreferences.visible);

        return normalizedPreferences.order
            .filter((key) => visibleKeys.has(key))
            .map((key) => headersMap.get(key))
            .filter(Boolean);
    }, [columnPreferences, tableHeaders]);

    const optimizedSortKeyMap = useMemo(() => {
        const map = {};
        sortOrders.forEach(order => {

            const parts = order.value.split('_');
            const direction = parts.pop().toLowerCase();
            const keyPrefix = parts.join('_');

            let headerKey;
            switch (keyPrefix) {
                case 'ID': headerKey = 'id'; break;
                case 'NAME': headerKey = 'first_name'; break;
                case 'SALE_STATUS': headerKey = 'sale_status'; break;
                case 'RETEN_STATUS': headerKey = 'reten_status'; break;
                case 'MANAGER_NAME': headerKey = 'manager_id'; break;
                case 'PHONE': headerKey = 'phone'; break;
                case 'EMAIL': headerKey = 'email'; break;
                case 'GEO': headerKey = 'geo'; break;
                case 'REGISTRATION_DATE': headerKey = 'created_at'; break;
                case 'AFFILIATOR': headerKey = 'affiliator_id'; break;
                case 'LAST_COMMENT_DATE': headerKey = 'last_commented_at'; break;
                case 'LAST_COMMENT_TEXT': headerKey = 'last_comment'; break;
                case 'CAMPAIGN': headerKey = 'campaign'; break;
                default: headerKey = null; break;
            }

            if (headerKey) {
                if (!map[headerKey]) {
                    map[headerKey] = {};
                }

                map[headerKey][direction] = order.value.toUpperCase();
            }
        });
        return map;
    }, []);

    useEffect(() => {
        let isMounted = true;

        const loadToolbarOptions = async () => {
            const options = await loadLeadFilterOptions(toolbarFilterConfigs, { langSelected });

            if (isMounted) {
                setToolbarFilterOptions(options);
            }
        };

        loadToolbarOptions();

        return () => {
            isMounted = false;
        };
    }, [toolbarFilterConfigs, langSelected]);

    const normalizeDate = (v) => {
        if (!v) return '';
        if (v instanceof Date) return v.toISOString().slice(0, 10);

        if (typeof v === 'object' && v.$d instanceof Date) {
            return v.$d.toISOString().slice(0, 10);
        }

        if (typeof v === 'object' && typeof v.toDate === 'function') {
            const d = v.toDate();
            return d instanceof Date ? d.toISOString().slice(0, 10) : '';
        }

        return String(v);
    };

    const persistLeadNavState = useCallback(() => {
        let order_by = filters.order_by || '';

        if (sortConfig.key && sortConfig.direction !== 'default') {
            const backendSortValue = optimizedSortKeyMap[sortConfig.key]?.[sortConfig.direction];
            if (backendSortValue) {
                order_by = backendSortValue;
            }
        }

        const params = {
            ...filters,
            order_by,
            from_date: normalizeDate(filters.from_date),
            to_date: normalizeDate(filters.to_date),
        };

        const payload = {
            role,
            page: currentPage,
            per_page: itemsPerPage,
            meta: {
                current_page: currentPage,
                last_page: totalPages,
                per_page: itemsPerPage,
                total: totalItems,
            },
            params,
            pageLeadIds: leads.map((l) => l.id),
            savedAt: Date.now(),
        };

        try {
            sessionStorage.setItem(`leadNavState_${role}`, JSON.stringify(payload));
        } catch (e) {
        }
    }, [
        role,
        currentPage,
        itemsPerPage,
        totalPages,
        totalItems,
        filters,
        sortConfig,
        optimizedSortKeyMap,
        leads,
    ]);

    const loadLeads = useCallback(async () => {
        if (role === 'admin' && (!isPermissionsLoaded || !permissions['lead.list_leads'])) {
            setIsLoading(false);
            setLeads([]);
            setTotalPages(1);
            return;
        }

        setIsLoading(true);
        setErrors({});
        try {
            let sortParam = {};

            if (sortConfig.key && sortConfig.direction !== 'default') {
                const backendSortValue = optimizedSortKeyMap[sortConfig.key]?.[sortConfig.direction];


                if (backendSortValue) {
                    sortParam = { order_by: backendSortValue };
                }
            }

            const paramsToFetch = {
                ...filters,
                ...sortParam,
                from_date: normalizeDate(filters.from_date),
                to_date: normalizeDate(filters.to_date),
            };

            const result = await fetchLeads(currentPage, paramsToFetch, itemsPerPage, role);
            const { data, meta } = result;
            setLeads(data);
            setTotalPages(meta.last_page);
            setCurrentPage(meta.current_page);
            setTotalItems(meta.total ?? 0);
        } catch (error) {
            handleError(error, setErrors);
            setLeads([]);
            setTotalPages(1);
        } finally {
            setIsLoading(false);
        }
    }, [
        currentPage,
        filters,
        itemsPerPage,
        role,
        isPermissionsLoaded,
        permissions,
        sortConfig,
        optimizedSortKeyMap
    ]);

    useEffect(() => {
        setSearchInput(filters.search || '');
    }, [filters.search]);

    useEffect(() => {
        const normalizedSearch = searchInput.trim();

        if ((filters.search || '') === normalizedSearch) {
            return;
        }

        const timeoutId = setTimeout(() => {
            let nextFilters;
            setFilters((prevFilters) => {
                nextFilters = {
                    ...prevFilters,
                    search: normalizedSearch,
                };

                return nextFilters;
            });
            setCurrentPage(1);

            if (nextFilters && localStorage.getItem(SAVE_FILTERS_SETTING_KEY) !== 'false') {
                saveWithTTL(LEAD_STORAGE_KEY_WITH_ROLE, getPersistedLeadFilters(nextFilters));
            }
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timeoutId);
    }, [searchInput, filters, LEAD_STORAGE_KEY_WITH_ROLE]);

    useEffect(() => {
        loadLeads();
    }, [loadLeads]);

    useEffect(() => {
        if (!shouldUpdateLeads) return;

        loadLeads().finally(() => {
            setShouldUpdateLeads(false);
        });
    }, [shouldUpdateLeads, loadLeads]);

    const handleSelectLead = (leadId, isSelected) => {
        setSelectedLeads(prevSelected => {
            const newSelected = new Set(prevSelected);
            if (isSelected) {
                newSelected.add(leadId);
            } else {
                newSelected.delete(leadId);
            }
            return newSelected;
        });
    };

    const handleSelectAll = (isSelected) => {
        setSelectedLeads(prevSelected => {
            const newSelected = new Set(prevSelected);
            if (isSelected) {
                leads.forEach(lead => newSelected.add(lead.id));
            } else {
                leads.forEach(lead => newSelected.delete(lead.id));
            }
            return newSelected;
        });
    };

    const isAllSelected = leads.length > 0 && leads.every(lead => selectedLeads.has(lead.id));
    const isAnySelected = selectedLeads.size > 0;

    const toggleBulkActions = () => {
        setShowBulkActions(prev => {
            if (prev) {
                setSelectedLeads(new Set());
            }
            return !prev;
        });
    };

    const handleDeleteSelected = () => {
        if (selectedLeads.size === 0) return;
        setIsBulkDeleteConfirmOpen(true);
    };

    const handleCloseBulkDeleteConfirm = () => {
        setIsBulkDeleteConfirmOpen(false);
    };

    const confirmBulkDelete = async () => {
        setIsBulkDeleting(true);
        setErrors({});
        const idsToDelete = Array.from(selectedLeads);

        try {
            await bulkDeleteLeadsApi(idsToDelete);

            setSelectedLeads(new Set());
            setShowBulkActions(false);
            setLeads(prevLeads => prevLeads.filter(lead => !idsToDelete.includes(lead.id)));
            setTotalPages(prevPages => Math.max(1, prevPages));

            if (idsToDelete.length === leads.length && currentPage > 1) {
                setCurrentPage(currentPage - 1);
            } else {
                loadLeads();
            }

        } catch (error) {
            handleError(error, setErrors);
        } finally {
            setIsBulkDeleting(false);
            setIsBulkDeleteConfirmOpen(false);
        }
    };

    const handleHardDeleteSelected = () => {
        if (!isSuperAdmin || selectedLeads.size === 0) return;
        setIsBulkHardDeleteConfirmOpen(true);
    };

    const handleCloseBulkHardDeleteConfirm = () => {
        if (!isBulkHardDeleting) {
            setIsBulkHardDeleteConfirmOpen(false);
        }
    };

    const confirmBulkHardDelete = async () => {
        if (!isSuperAdmin || isBulkHardDeleting) return;

        setIsBulkHardDeleting(true);
        setErrors({});
        const idsToDelete = Array.from(selectedLeads);

        try {
            await bulkHardDeleteLeadsApi(idsToDelete);

            setSelectedLeads(new Set());
            setShowBulkActions(false);
            setLeads(prevLeads => prevLeads.filter(lead => !idsToDelete.includes(lead.id)));
            setTotalPages(prevPages => Math.max(1, prevPages));

            if (idsToDelete.length === leads.length && currentPage > 1) {
                setCurrentPage(currentPage - 1);
            } else {
                loadLeads();
            }
        } catch (error) {
            handleError(error, setErrors);
            setSelectedLeads(new Set());
            loadLeads();
        } finally {
            setIsBulkHardDeleting(false);
            setIsBulkHardDeleteConfirmOpen(false);
        }
    };

    const handleFirstPage = () => {
        setCurrentPage(1);
    };

    const handleLastPage = () => {
        setCurrentPage(totalPages);
    };

    const handlePageClick = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const handleHeaderClick = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key) {
            if (sortConfig.direction === 'asc') {
                direction = 'desc';
            } else if (sortConfig.direction === 'desc') {
                direction = 'default';
            } else {
                direction = 'asc';
            }
        }

        const nextSortConfig = { key, direction };
        const nextOrderBy = direction === 'default'
            ? ''
            : optimizedSortKeyMap[key]?.[direction] || '';

        const nextFilters = {
            ...filters,
            order_by: nextOrderBy,
        };

        setSortConfig(nextSortConfig);
        setFilters(nextFilters);

        if (localStorage.getItem(SAVE_FILTERS_SETTING_KEY) !== 'false') {
            saveWithTTL(LEAD_STORAGE_KEY_WITH_ROLE, getPersistedLeadFilters(nextFilters));
        }

        setCurrentPage(1);
    };

    const getSortIcon = (key) => {
        if (sortConfig.key === key) {
            if (sortConfig.direction === 'asc') {
                return <ArrowUpwardIcon fontSize="small" />;
            } else if (sortConfig.direction === 'desc') {
                return <ArrowDownwardIcon fontSize="small" />;
            }
        }
        return <UnfoldMoreIcon fontSize="small" />;
    };

    const renderPaginationButtons = () => {
        const pages = [];
        const maxPageButtons = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
        let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

        if (endPage - startPage + 1 < maxPageButtons) {
            startPage = Math.max(1, endPage - maxPageButtons + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    className={`${styles.paginationButton} ${i === currentPage ? styles.activePage : ''} ${theme === 'dark' ? styles.darkPaginationButton : ''}`}
                    onClick={() => handlePageClick(i)}
                    disabled={isLoading}
                >
                    {i}
                </button>
            );
        }
        return pages;
    };

    const handleClickFilter = () => {
        setIsActiveFilter(true);
    };

    const handleOpenTableSettingsModal = () => {
        setIsTableSettingsModalOpen(true);
    };

    const handleCloseTableSettingsModal = () => {
        setIsTableSettingsModalOpen(false);
    };

    const handleSaveTableSettings = (nextPreferences) => {
        setColumnPreferences(normalizeColumnPreferences(nextPreferences, tableHeaders));
        setIsTableSettingsModalOpen(false);
    };

    const handleApplyFilters = (newFilters) => {
        setFilters(newFilters);
        setSortConfig(getSortConfigFromOrderBy(newFilters.order_by));
        setCurrentPage(1);
        setIsActiveFilter(false);
    };

    const handleLeadCreated = () => {
        setShouldUpdateLeads(true);
        setModalAddLead(false);
    };

    const handleOpenLeadCommentModal = (lead) => {
        setSelectedLeadForComment(lead);
    };

    const handleCloseLeadCommentModal = () => {
        setSelectedLeadForComment(null);
    };

    const handleOpenLeadStatusModal = (lead, statusKey) => {
        setSelectedLeadStatusModal({ lead, statusKey });
    };

    const handleCloseLeadStatusModal = () => {
        setSelectedLeadStatusModal(null);
    };

    const handleOpenLeadManagerModal = (lead) => {
        setSelectedLeadManagerModal(lead);
    };

    const handleCloseLeadManagerModal = () => {
        setSelectedLeadManagerModal(null);
    };

    const handleInteractiveCellMouseDown = (event) => {
        interactiveCellPointerRef.current = {
            pointerType: event.button,
            startX: event.clientX,
            startY: event.clientY,
            moved: false,
        };
    };

    const handleInteractiveCellMouseMove = (event) => {
        const pointerState = interactiveCellPointerRef.current;

        if (pointerState.pointerType !== 0 || pointerState.moved) {
            return;
        }

        const movedX = Math.abs(event.clientX - pointerState.startX);
        const movedY = Math.abs(event.clientY - pointerState.startY);

        if (movedX > 4 || movedY > 4) {
            interactiveCellPointerRef.current = {
                ...pointerState,
                moved: true,
            };
        }
    };

    const shouldIgnoreInteractiveCellClick = () => {
        const pointerState = interactiveCellPointerRef.current;
        interactiveCellPointerRef.current = {
            pointerType: null,
            startX: 0,
            startY: 0,
            moved: false,
        };

        if (pointerState.pointerType !== 0 || pointerState.moved) {
            return true;
        }

        const selection = window.getSelection?.();
        return !!selection && !selection.isCollapsed && selection.toString().trim().length > 0;
    };

    const handleLastCommentCellClick = (event, lead) => {
        if (!(role === 'admin' ? permissions['lead.create_lead_comments'] : true)) {
            return;
        }

        if (!canEditLeadField('last_comment')) {
            return;
        }

        if (shouldIgnoreInteractiveCellClick()) {
            return;
        }

        event.stopPropagation();
        handleOpenLeadCommentModal(lead);
    };

    const handleLeadStatusCellClick = (event, lead, statusKey) => {
        if (!(role === 'admin' ? permissions['lead.update_leads'] : true)) {
            return;
        }

        if (!canEditLeadField(statusKey)) {
            return;
        }

        if (shouldIgnoreInteractiveCellClick()) {
            return;
        }

        event.stopPropagation();
        handleOpenLeadStatusModal(lead, statusKey);
    };

    const handleLeadManagerCellClick = (event, lead) => {
        if (!(role === 'admin' ? permissions['lead.update_leads'] : false)) {
            return;
        }

        if (!canEditLeadField('manager_id')) {
            return;
        }

        if (shouldIgnoreInteractiveCellClick()) {
            return;
        }

        event.stopPropagation();
        handleOpenLeadManagerModal(lead);
    };

    const handleItemsPerPageChange = (name, value) => {
        const newValue = parseInt(value, 10);
        if (!isNaN(newValue)) {
            setItemsPerPage(newValue);
            localStorage.setItem(ITEMS_PER_PAGE_KEY, newValue.toString());
            setCurrentPage(1);
        }
    };

    const handleSearchChange = (event) => {
        setSearchInput(event.target.value);
    };

    const handleToolbarFilterChange = useCallback((filterName, value, filterKey) => {
        void filterName;
        const normalizedValue = Array.isArray(value) ? value : [];
        let nextFilters;

        setFilters((prevFilters) => {
            nextFilters = {
                ...prevFilters,
                [filterKey]: normalizedValue,
            };

            if (normalizedValue.length === 0) {
                delete nextFilters[filterKey];
            }

            return nextFilters;
        });

        if (nextFilters && localStorage.getItem(SAVE_FILTERS_SETTING_KEY) !== 'false') {
            saveWithTTL(LEAD_STORAGE_KEY_WITH_ROLE, getPersistedLeadFilters(nextFilters));
        }

        setCurrentPage(1);
    }, [LEAD_STORAGE_KEY_WITH_ROLE]);

    const renderTableCell = (lead, header) => {
        const isDeleted = !!lead.deleted_at;
        let content = lead?.[header.key];

        if (header.key === 'first_name') {
            content = (
                <span className={styles.leadNameCell}>
                    <span
                        className={`${styles.leadOnlineDot} ${lead.is_online ? styles.leadOnlineDotOnline : styles.leadOnlineDotOffline}`}
                        title={lead.is_online ? 'Online' : 'Offline'}
                        aria-label={lead.is_online ? 'Online' : 'Offline'}
                    />
                    <NavLink
                        to={`/lead/change/info/${lead.id}`}
                        className={`${styles.leadLink} ${theme === 'dark' ? styles.darkLeadLink : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            persistLeadNavState();
                        }}
                    >
                        {lead.first_name} {lead.last_name}
                    </NavLink>
                </span>
            );
        } else if (header.key === 'manager_id') {
            content = (lead.manager?.first_name || lead.manager?.last_name) ? `${lead.manager?.first_name || ''} ${lead.manager?.last_name || ''}`.trim() : '';

            if (role === 'admin' && !permissions['lead.update_leads']) {
                return content;
            }

            return (
                <span className={`${styles.managerValue} ${!content ? styles.managerEmpty : ''}`}>
                    {content}
                </span>
            );
        } else if (header.key === 'affiliator_id') {
            content = (lead.affiliator?.first_name ? `${lead.affiliator?.first_name} ${lead.affiliator?.last_name || ''}`.trim() : lead.affiliator?.affiliate) || lead.affiliator?.email || "";
        } else if (header.key === 'campaign') {
            content = lead.campaign?.name || lead.campaign || '';
        } else if (header.key === 'geo') {
            const geoName = getGeoCountryName(lead.geo, langSelected);
            const geoFlagPath = getGeoFlagPath(lead.geo);

            content = (
                <span className={styles.geoCell}>
                    {geoFlagPath && (
                        <img
                            src={geoFlagPath}
                            alt={geoName}
                            className={styles.geoFlag}
                            loading="lazy"
                        />
                    )}
                    <span>{geoName}</span>
                </span>
            );
        } else if (header.key === 'created_at' || header.key === 'last_commented_at') {
            content = formatDateTime(lead[header.key]);
        } else if (header.key === 'last_comment') {
            const trimmedComment = trimToWords(lead[header.key], 50);
            content = trimmedComment || currentTranslation.emptyCommentCell;

            if (role === 'admin' && !permissions['lead.create_lead_comments']) {
                return content;
            }

            content = (
                <span className={`${styles.lastCommentValue} ${!trimmedComment ? styles.lastCommentEmpty : ''}`}>
                    {content}
                </span>
            );
        } else if (CLICKABLE_STATUS_KEYS.has(header.key)) {
            const statusValue = lead[header.key];
            const statusBadgeClassName = getLeadStatusBadgeClassName(statusValue);
            content = statusValue || '';

            if (statusValue) {
                content = (
                    <span className={`${styles.statusValue} ${statusBadgeClassName ? `${styles.statusBadge} ${styles[statusBadgeClassName]}` : ''}`}>
                        {content}
                    </span>
                );
            }
        } else if (typeof lead?.[header.key] === 'object' && lead?.[header.key] !== null) {
            content = lead?.[header.key]?.name || lead?.[header.key]?.value || lead?.[header.key]?.id;
        }

        if (role === 'admin' && header.key === 'id') {
            content = (
                <div className={styles.idCellContent}>
                    {lead.id}
                    {isDeleted && (
                        <DeleteForeverIcon
                            fontSize="small"
                            className={styles.deletedIcon}
                            title={`Удалено: ${formatDateTime(lead.deleted_at)}`}
                        />
                    )}
                </div>
            );
        }

        return content;
    };

    const hasActiveFilters = useMemo(() => {
        if (!filters) return false;
        const keys = Object.keys(filters).filter(key => key !== 'order_by');
        return keys.some(key => {
            const value = filters[key];
            if (Array.isArray(value)) return value.length > 0;
            return value !== '' && value !== null && value !== undefined;
        });
    }, [filters]);


    if (role === 'admin' && !isPermissionsLoaded) {
        return (
            <div className={`${styles.loadingOverlay} ${theme === 'dark' ? styles.darkLoadingOverlay : ''}`}>
                <LoadingCircle size={40} thickness={3} />
            </div>
        );
    }

    if (role === 'admin' && !permissions['lead.list_leads']) {
        return (
            <>
                <PermissionDenied
                    text={currentTranslation.accessDenied}
                />
            </>
        );
    }

    const canListAffiliatorAccounts = role === 'admin' && !!permissions['user.list_affiliator_accounts'];
    const canBulkUpdate = permissions['lead.update_leads'];
    const canBulkDelete = permissions['lead.delete_leads'];
    const canBulkHardDelete = role === 'admin' && isSuperAdmin;
    const canCreateLeads = role === 'admin' ? !!permissions['lead.create_leads'] : true;
    const canCreateLeadComments = role === 'admin' ? !!permissions['lead.create_lead_comments'] : true;
    const canUpdateLeadStatuses = role === 'admin' ? !!permissions['lead.update_leads'] : true;
    const canUpdateLeadManager = role === 'admin' ? !!permissions['lead.update_leads'] : false;

    return (
        <div className={`${styles.container} ${theme === 'dark' ? styles.darkContainer : ''}`}>
            {errors.general && <Notification message={errors.general} type={"error"} />}

            {importResultMessage && (
                <Notification message={importResultMessage} type={"error"} />
            )}

            <Title h2={currentTranslation.title} inlineNotifications />
            <div className={`${styles.content} ${theme === 'dark' ? styles.darkContent : ''}`}>

                <div className={styles.topBar}>
                    <div className={styles.itemsPerPageSelector}>
                        <DropdownSelector
                            filterName="itemsPerPage"
                            filterKey="itemsPerPage"
                            selectedValue={itemsPerPage}
                            handleFilterChange={handleItemsPerPageChange}
                            options={leadsPerPageOptions}
                            hasError={false}
                            errorMessage=""
                        />
                    </div>
                    <div className={styles.action}>
                        {(canBulkUpdate || canBulkDelete || canBulkHardDelete) && (
                            <div className={styles.bulkActionButtonContainer}>
                                <button
                                    className={`${styles.bulkActionButton} ${showBulkActions ? styles.bulkActionActive : ''} ${theme === 'dark' ? styles.darkBulkActionButton : ''}`}
                                    onClick={toggleBulkActions}
                                >
                                    {currentTranslation.bulkActions}
                                </button>
                            </div>
                        )}

                        {canCreateLeads && (
                            <div className={styles.addButtonContainer}>
                                <button className={`${styles.addButton} ${theme === 'dark' ? styles.darkAddButton : ''}`} onClick={() => setModalAddLead(true)}>
                                    <AddIcon fontSize="small" />
                                    <p className={styles.addButtonText}>{currentTranslation.addLead}</p>
                                </button>
                            </div>
                        )}

                        {canCreateLeads && (
                            <div className={styles.addButtonContainer}>
                                {canCreateLeads && (
                                    <button
                                        className={styles.addButton}
                                        onClick={() => setIsImportModalOpen(true)}
                                        style={{ marginLeft: '8px' }}
                                    >
                                        <CloudUploadIcon fontSize="small" />
                                        <p className={styles.addButtonText}>{currentTranslation.importLeads}</p>
                                    </button>
                                )}
                            </div>
                        )}

                        {isIpAdmin && (
                            <div className={styles.addButtonContainer}>
                                <button
                                    className={styles.addButton}
                                    onClick={() => setIsExportModalOpen(true)}
                                    style={{ marginLeft: '8px' }}
                                >
                                    <CloudDownloadIcon fontSize="small" />
                                    <p className={styles.addButtonText}>{currentTranslation.exportLeads}</p>
                                </button>
                            </div>
                        )}

                        <div className={styles.filterButtonContainer}>
                            <div className={styles.filterControls}>
                                <button
                                    className={`${styles.filterButton} ${hasActiveFilters ? styles.activeFilterButton : ''} ${theme === 'dark' ? styles.darkFilterButton : ''}`}
                                    onClick={handleClickFilter}
                                >
                                    <FilterListIcon fontSize="small" />
                                    <p className={styles.filterButtonText}>{currentTranslation.filters}</p>
                                    {hasActiveFilters && <span className={`${styles.activeFilterDot} ${theme === 'dark' ? styles.darkActiveFilterDot : ''}`} />}
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.tableSettingsButton} ${theme === 'dark' ? styles.darkTableSettingsButton : ''}`}
                                    onClick={handleOpenTableSettingsModal}
                                    aria-label={currentTranslation.tableSettings}
                                    title={currentTranslation.tableSettings}
                                >
                                    <SettingsIcon fontSize="small" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>


                {showBulkActions && (
                    <div className={`${styles.bulkActionsPanel} ${theme === 'dark' ? styles.darkBulkActionsPanel : ''}`}>
                        <div className={`${styles.selectAllContainer} ${theme === 'dark' ? styles.darkSelectAllContainer : ''}`}>
                            <input
                                type="checkbox"
                                checked={isAllSelected}
                                onChange={(e) => handleSelectAll(e.target.checked)}
                                id="selectAll"
                            />
                            <label htmlFor="selectAll" onClick={() => handleSelectAll(!isAllSelected)}>
                                {currentTranslation.selectAll} ({selectedLeads.size} / {leads.length})
                            </label>
                        </div>

                        <div className={styles.bulkActionButtons}>
                            {canBulkUpdate && (
                                <button
                                    className={styles.bulkUpdateButton}
                                    disabled={!isAnySelected}
                                    onClick={() => setIsBulkUpdating(true)}
                                >
                                    {currentTranslation.bulkUpdateSelected} ({selectedLeads.size})
                                </button>
                            )}
                            {canBulkDelete && (
                                <button
                                    className={styles.bulkDeleteButton}
                                    disabled={!isAnySelected || isBulkDeleting}
                                    onClick={handleDeleteSelected}
                                >
                                    {isBulkDeleting ? currentTranslation.deleting : currentTranslation.deleteConfirmText} ({selectedLeads.size})
                                </button>
                            )}
                            {canBulkHardDelete && (
                                <button
                                    className={styles.bulkDeleteButton}
                                    disabled={!isAnySelected || isBulkHardDeleting}
                                    onClick={handleHardDeleteSelected}
                                >
                                    {isBulkHardDeleting ? currentTranslation.deleting : currentTranslation.hardDeleteConfirmText} ({selectedLeads.size})
                                </button>
                            )}
                        </div>
                    </div>
                )}

                <div className={styles.searchAndFiltersRow}>
                    <div className={`${styles.searchBar} ${theme === 'dark' ? styles.darkSearchBar : ''}`}>
                        <SearchIcon className={`${styles.searchIcon} ${theme === 'dark' ? styles.darkSearchIcon : ''}`} fontSize="small" />
                        <input
                            type="text"
                            value={searchInput}
                            onChange={handleSearchChange}
                            placeholder={currentTranslation.searchPlaceholder}
                            className={`${styles.searchInput} ${theme === 'dark' ? styles.darkSearchInput : ''}`}
                        />
                    </div>

                    <div className={styles.inlineFilters}>
                        {toolbarFilterConfigs.map(({ key, formKey }, index) => (
                            <div key={key} className={styles.inlineFilterItem}>
                                <MultiSelectDropdown
                                    filterName={key}
                                    filterKey={formKey}
                                    selectedValue={Array.isArray(filters[formKey]) ? filters[formKey] : []}
                                    handleFilterChange={handleToolbarFilterChange}
                                    options={toolbarFilterOptions[key] || []}
                                    placeholder={currentFieldLabels[key] || key}
                                    dropdownAlign={index === toolbarFilterConfigs.length - 1 ? 'right' : 'left'}
                                    shouldAutoClearOnMissingOptions={false}
                                    isSearchable
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {isLoading ? (
                    <div className={styles.loadingBlock}>
                        <LoadingCircle size={30} thickness={1} />
                    </div>
                ) : (
                    <div className={styles.tableWrapper}>
                        <table className={styles.table}>
                            <thead className={`${styles.tableHeader} ${theme === 'dark' ? styles.darkTableHeader : ''}`}>
                                <tr>

                                        {showBulkActions && (
                                            <th className={styles.tableHeaderTh} style={{ width: '40px' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={isAllSelected}
                                                    onChange={(e) => handleSelectAll(e.target.checked)}
                                                />
                                            </th>
                                        )}
                                        {visibleTableHeaders.map((header) => (
                                            <th
                                                key={header.key}
                                                onClick={() => handleHeaderClick(header.key)}
                                                className={`${styles.sortableHeader} ${sortConfig.key === header.key && sortConfig.direction !== 'default' ? styles.activeSortHeader : ''} ${theme === 'dark' ? styles.darkSortableHeader : ''}`}
                                            >
                                                <div className={styles.sortableElement}>
                                                    {header.label}
                                                    <span className={`${styles.sortIcon} ${theme === 'dark' ? styles.darkSortIcon : ''}`}>
                                                        {getSortIcon(header.key)}
                                                    </span>
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {leads.length === 0 ? (
                                        <tr>
                                            <td colSpan={visibleTableHeaders.length + (showBulkActions ? 1 : 0)} className={`${styles.noLeadsMessage} ${theme === 'dark' ? styles.darkNoLeadsMessage : ''}`}>
                                                {currentTranslation.noLeadsFound}
                                            </td>
                                        </tr>
                                    ) : (
                                        leads.map((lead) => {
                                            const isLeadSelected = selectedLeads.has(lead.id);
                                            const isDeleted = !!lead.deleted_at;
                                            return (
                                                <tr
                                                    key={lead.id}
                                                    className={`${styles.tableRow} ${isLeadSelected ? styles.selectedRow : ''} ${isDeleted ? styles.deletedRow : ''} ${theme === 'dark' ? styles.darkTableRow : ''}`}
                                                    onClick={(e) => {
                                                        if (e.target.closest('a')) return;
                                                        if (showBulkActions) {
                                                            handleSelectLead(lead.id, !isLeadSelected);
                                                        }
                                                    }}
                                                >

                                                    {showBulkActions && (
                                                        <td className={`${styles.tableCell} ${theme === 'dark' ? styles.darkTableCell : ''}`} onClick={(e) => e.stopPropagation()}>
                                                            <input
                                                                type="checkbox"
                                                                checked={isLeadSelected}
                                                                onChange={(e) => handleSelectLead(lead.id, e.target.checked)}
                                                            />
                                                        </td>
                                                    )}
                                                    {visibleTableHeaders.map((header) => {
                                                        const isCommentEditable = header.key === 'last_comment'
                                                            && canCreateLeadComments
                                                            && canEditLeadField('last_comment');
                                                        const isStatusEditable = CLICKABLE_STATUS_KEYS.has(header.key)
                                                            && canUpdateLeadStatuses
                                                            && canEditLeadField(header.key);
                                                        const isManagerEditable = header.key === 'manager_id'
                                                            && canUpdateLeadManager
                                                            && canEditLeadField('manager_id');
                                                        const isInteractiveCell = isCommentEditable || isStatusEditable || isManagerEditable;

                                                        return (
                                                            <td
                                                                key={`${lead.id}-${header.key}`}
                                                                className={`${styles.tableCell} ${theme === 'dark' ? styles.darkTableCell : ''} ${isCommentEditable ? styles.lastCommentCell : ''} ${isStatusEditable ? styles.statusCell : ''} ${isManagerEditable ? styles.managerCell : ''}`}
                                                                onMouseDown={isInteractiveCell ? handleInteractiveCellMouseDown : undefined}
                                                                onMouseMove={isInteractiveCell ? handleInteractiveCellMouseMove : undefined}
                                                                onClick={isCommentEditable
                                                                    ? (event) => handleLastCommentCellClick(event, lead)
                                                                    : isStatusEditable
                                                                        ? (event) => handleLeadStatusCellClick(event, lead, header.key)
                                                                        : isManagerEditable
                                                                            ? (event) => handleLeadManagerCellClick(event, lead)
                                                                            : undefined}
                                                            >
                                                                {renderTableCell(lead, header)}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            )
                                        })
                                    )}
                                </tbody>
                        </table>
                    </div>
                )}

                {!isLoading && totalPages > 1 && (
                    <div className={`${styles.pagination} ${theme === 'dark' ? styles.darkPagination : ''}`}>
                        <button
                            className={`${styles.paginationButton} ${theme === 'dark' ? styles.darkPaginationButton : ''}`}
                            onClick={handleFirstPage}
                            disabled={currentPage === 1 || isLoading}
                        >
                            {currentTranslation.first}
                        </button>
                        <div className={`${styles.pageButtonsWrapper} ${theme === 'dark' ? styles.darkPageButtonsWrapper : ''}`}>
                            {renderPaginationButtons()}
                        </div>
                        <button
                            className={`${styles.paginationButton} ${theme === 'dark' ? styles.darkPaginationButton : ''}`}
                            onClick={handleLastPage}
                            disabled={currentPage === totalPages || isLoading}
                        >
                            {currentTranslation.last}
                        </button>
                    </div>
                )}
            </div>

            {modalAddLead && (role === 'admin' ? permissions['lead.create_leads'] : true) && (
                <AddLeadModal setModalAddLead={setModalAddLead} role={role} onLeadCreated={handleLeadCreated} />
            )}

            {isImportModalOpen && canCreateLeads && (
                <ImportLeadsModal
                    onClose={() => setIsImportModalOpen(false)}
                    onSuccess={(result) => {
                        setIsImportModalOpen(false);
                        setShouldUpdateLeads(true);

                        if (!result) {
                            return;
                        }

                        const {
                            total_rows,
                            created,
                            skipped,
                            errors: importErrors = [],
                        } = result;

                        if (Array.isArray(importErrors) && importErrors.length > 0) {
                            const summarizedErrors = importErrors.slice(0, 5).map((err) => {
                                const emailPart = err.email ? ` (${err.email})` : '';
                                return `Row ${err.row}${emailPart}: ${err.error}`;
                            });

                            const remainingCount = importErrors.length - summarizedErrors.length;
                            const moreText = remainingCount > 0 ? ` (+${remainingCount} more)` : '';

                            const message =
                                `Imported: ${created}/${total_rows}, skipped: ${skipped}. ` +
                                `Errors: ${summarizedErrors.join(' | ')}${moreText}`;

                            setImportResultMessage(message);
                        } else {
                            const message = `Imported: ${created}/${total_rows}, skipped: ${skipped}.`;
                            setImportResultMessage(message);
                        }
                    }}
                />
            )}

            {selectedLeadForComment && canCreateLeadComments && (
                <LeadCommentModal
                    lead={selectedLeadForComment}
                    role={role}
                    onClose={handleCloseLeadCommentModal}
                    onSuccess={loadLeads}
                />
            )}

            {selectedLeadStatusModal && canUpdateLeadStatuses && (
                <LeadStatusModal
                    lead={selectedLeadStatusModal.lead}
                    role={role}
                    statusKey={selectedLeadStatusModal.statusKey}
                    onClose={handleCloseLeadStatusModal}
                    onSuccess={loadLeads}
                />
            )}

            {selectedLeadManagerModal && canUpdateLeadManager && (
                <LeadManagerModal
                    lead={selectedLeadManagerModal}
                    role={role}
                    onClose={handleCloseLeadManagerModal}
                    onSuccess={loadLeads}
                />
            )}

            {isExportModalOpen && (
                    <ExportLeadsModal
                        onClose={() => setIsExportModalOpen(false)}
                        initialFilters={exportInitialFilters}
                        onSuccess={() => {
                            setIsExportModalOpen(false);
                        }}
                    />
                )}

            {isActiveFilter && (
                <ModalFilter
                    isActiveFilter={isActiveFilter}
                    setIsActiveFilter={setIsActiveFilter}
                    filters={filters}
                    onApplyFilters={handleApplyFilters}
                    role={role}
                    canListAffiliatorAccounts={canListAffiliatorAccounts}
                />
            )}

            {isTableSettingsModalOpen && (
                <LeadTableSettingsModal
                    isOpen={isTableSettingsModalOpen}
                    onClose={handleCloseTableSettingsModal}
                    columns={tableHeaders}
                    preferences={normalizeColumnPreferences(columnPreferences, tableHeaders)}
                    onSave={handleSaveTableSettings}
                />
            )}


            {isBulkUpdating && isAnySelected && (
                <BulkUpdateModal
                    leadIds={Array.from(selectedLeads)}
                    onClose={() => setIsBulkUpdating(false)}
                    onSuccess={loadLeads}
                />
            )}


            {isBulkDeleteConfirmOpen && (
                <ConfirmationModal
                    isOpen={isBulkDeleteConfirmOpen}
                    onClose={handleCloseBulkDeleteConfirm}
                    onConfirm={confirmBulkDelete}
                    title={currentTranslation.deleteConfirmTitle}
                    message={currentTranslation.confirmBulkDelete(selectedLeads.size)}
                    confirmText={isBulkDeleting ? currentTranslation.deleting : currentTranslation.deleteConfirmText}
                    cancelText={currentTranslation.cancel}
                />
            )}

            {isBulkHardDeleteConfirmOpen && (
                <ConfirmationModal
                    isOpen={isBulkHardDeleteConfirmOpen}
                    onClose={handleCloseBulkHardDeleteConfirm}
                    onConfirm={confirmBulkHardDelete}
                    title={currentTranslation.hardDeleteConfirmTitle}
                    message={currentTranslation.confirmBulkHardDelete(selectedLeads.size)}
                    confirmText={isBulkHardDeleting ? currentTranslation.deleting : currentTranslation.hardDeleteConfirmText}
                    cancelText={currentTranslation.cancel}
                />
            )}
        </div>
    );
};
